import { Routes, Route, Navigate } from 'react-router-dom'
import { PageTabs } from '@/components/shared/PageTabs'
import { HomeRoute } from '@/routes/HomeRoute'
import { StudentAware } from '@/routes/StudentAware'
import { Component, lazy, Suspense, type ReactNode, type ErrorInfo } from 'react'
import * as Sentry from '@sentry/react'
import { useAuth } from '@/hooks/useAuth'
import { BrandLoadingScreen } from '@/components/shared/BrandLoadingScreen'
import { OfflineBanner } from '@/components/shared/OfflineBanner'
import { PlatformRatesSync } from '@/hooks/usePlatformSettings'
import { PwaInstallBanner } from '@/components/shared/PwaInstallBanner'
import { CookieConsent } from '@/components/shared/CookieConsent'

// ─── Error Boundary ───────────────────────────────────────────────────────────

interface EBState { error: Error | null }

class AppErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  state: EBState = { error: null }

  static getDerivedStateFromError(error: Error): EBState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
    if (import.meta.env.DEV) {
      console.error('[AppErrorBoundary]', error, info.componentStack)
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
          <h1 className="text-2xl font-bold text-destructive">Algo deu errado</h1>
          <p className="text-sm text-muted-foreground max-w-sm">
            {this.state.error.message}
          </p>
          <button
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            onClick={() => { this.setState({ error: null }); window.location.href = '/' }}
          >
            Voltar para o início
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// Public pages
const InstructorsPage = lazy(() => import('@/pages/public/InstructorsPage'))
const InstructorProfilePage = lazy(() => import('@/pages/public/InstructorProfilePage'))
const CertificateVerifyPage = lazy(() => import('@/pages/public/CertificateVerifyPage'))
const CityInstructorsPage = lazy(() => import('@/pages/public/CityInstructorsPage'))
const LoginPage = lazy(() => import('@/pages/public/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/public/RegisterPage'))
const HowItWorksPage = lazy(() => import('@/pages/public/HowItWorksPage'))
const BeInstructorPage = lazy(() => import('@/pages/public/BeInstructorPage'))
const ResetPasswordPage = lazy(() => import('@/pages/public/ResetPasswordPage'))
const AuthCallbackPage = lazy(() => import('@/pages/public/AuthCallbackPage'))
const NotFoundPage = lazy(() => import('@/pages/public/NotFoundPage'))
const PixPaymentPage = lazy(() => import('@/pages/public/PixPaymentPage'))

// Student pages
const StudentDashboard = lazy(() => import('@/pages/student/StudentDashboard'))
const BookingFlow = lazy(() => import('@/pages/student/BookingFlow'))
const StudentBookingDetail = lazy(() => import('@/pages/student/StudentBookingDetail'))
const StudentProfile = lazy(() => import('@/pages/student/StudentProfile'))
const StudentReviews = lazy(() => import('@/pages/student/StudentReviews'))
const StudentOnboarding = lazy(() => import('@/pages/student/StudentOnboarding'))

// Instructor pages
const InstructorDashboard = lazy(() => import('@/pages/instructor/InstructorDashboard'))
const InstructorAgenda = lazy(() => import('@/pages/instructor/InstructorAgenda'))
const InstructorLessons = lazy(() => import('@/pages/instructor/InstructorLessons'))
const InstructorFinancial = lazy(() => import('@/pages/instructor/InstructorFinancial'))
const InstructorEditProfile = lazy(() => import('@/pages/instructor/InstructorEditProfile'))
const InstructorOnboarding = lazy(() => import('@/pages/instructor/InstructorOnboarding'))
const InstructorStudents = lazy(() => import('@/pages/instructor/InstructorStudents'))

// Public extra pages
const HelpPage = lazy(() => import('@/pages/public/HelpPage'))
const CancellationPolicyPage = lazy(() => import('@/pages/public/CancellationPolicyPage'))
const SecurityPage = lazy(() => import('@/pages/public/SecurityPage'))
const TermsPage = lazy(() => import('@/pages/public/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/public/PrivacyPage'))
const GiftCardPage = lazy(() => import('@/pages/public/GiftCardPage'))

// Shared pages
const NotificationsPage = lazy(() => import('@/pages/shared/NotificationsPage'))
const MessagesPage = lazy(() => import('@/pages/shared/MessagesPage'))

// Student extra pages
const StudentPayments = lazy(() => import('@/pages/student/StudentPayments'))
const StudentPremium = lazy(() => import('@/pages/student/StudentPremium'))
const StudentQuiz = lazy(() => import('@/pages/student/StudentQuiz'))
const StudentPackages = lazy(() => import('@/pages/student/StudentPackages'))
const StudentWallet = lazy(() => import('@/pages/student/StudentWallet'))
const StudentProgress = lazy(() => import('@/pages/student/StudentProgress'))
const StudentReferrals = lazy(() => import('@/pages/student/StudentReferrals'))

// Instructor extra pages
const InstructorReport = lazy(() => import('@/pages/instructor/InstructorReport'))
const InstructorEarnings = lazy(() => import('@/pages/instructor/InstructorEarnings'))
const InstructorIssueCertificate = lazy(() => import('@/pages/instructor/InstructorIssueCertificate'))

// Student extra pages (M3)
const StudentCertificates = lazy(() => import('@/pages/student/StudentCertificates'))

// Batch Q — novas páginas
const StudentHistory = lazy(() => import('@/pages/student/StudentHistory'))

// Batch R — novas páginas
const StudentSupport = lazy(() => import('@/pages/student/StudentSupport'))
const InstructorVehicles = lazy(() => import('@/pages/instructor/InstructorVehicles'))
const InstructorSupport = lazy(() => import('@/pages/instructor/InstructorSupport'))
const AdminSupport = lazy(() => import('@/pages/admin/AdminSupport'))

// Batch S — novas páginas
const InstructorGallery = lazy(() => import('@/pages/instructor/InstructorGallery'))

// Batch T — novas páginas
const FaqPage = lazy(() => import('@/pages/public/FaqPage'))
const AdminFaq = lazy(() => import('@/pages/admin/AdminFaq'))
const StudentNotificationSettings = lazy(() => import('@/pages/student/StudentNotificationSettings'))
const InstructorNotificationSettings = lazy(() => import('@/pages/instructor/InstructorNotificationSettings'))

// Batch U — novas páginas
const InstructorChatSettings = lazy(() => import('@/pages/instructor/InstructorChatSettings'))

// Batch V — novas páginas
const GoogleCalendarCallbackPage = lazy(() => import('@/pages/public/GoogleCalendarCallbackPage'))
const AdminNotifications = lazy(() => import('@/pages/admin/AdminNotifications'))
const AdminLgpd = lazy(() => import('@/pages/admin/AdminLgpd'))

// Admin extra pages (M2)
const AdminInstructorApproval = lazy(() => import('@/pages/admin/AdminInstructorApproval'))

// Admin extra pages (Batch N)
const AdminExports = lazy(() => import('@/pages/admin/AdminExports'))

// Batch O — novas páginas
const InstructorPackages = lazy(() => import('@/pages/instructor/InstructorPackages'))
const AdminPayouts = lazy(() => import('@/pages/admin/AdminPayouts'))

// Batch P — novas páginas
const AdminAnnouncements = lazy(() => import('@/pages/admin/AdminAnnouncements'))
const AdminAuditLog = lazy(() => import('@/pages/admin/AdminAuditLog'))

// Admin pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'))
const AdminInstructors = lazy(() => import('@/pages/admin/AdminInstructors'))
const AdminDisputes = lazy(() => import('@/pages/admin/AdminDisputes'))
const AdminFinancial = lazy(() => import('@/pages/admin/AdminFinancial'))
const AdminCoupons = lazy(() => import('@/pages/admin/AdminCoupons'))
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'))
const AdminUserDetail = lazy(() => import('@/pages/admin/AdminUserDetail'))
const AdminPlatformSettings = lazy(() => import('@/pages/admin/AdminPlatformSettings'))
const AdminAnalytics = lazy(() => import('@/pages/admin/AdminAnalytics'))

// Route guards
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import { RoleRoute } from '@/routes/RoleRoute'

// Layouts
import PublicLayout from '@/components/layout/PublicLayout'
import StudentLayout from '@/components/layout/StudentLayout'
import InstructorLayout from '@/components/layout/InstructorLayout'
import AdminLayout from '@/components/layout/AdminLayout'

function App() {
  const { initialized } = useAuth()

  // Null-render for the brief moment before INITIAL_SESSION fires from localStorage
  // (~1-2 frames, imperceptible). After that, public routes render immediately even
  // while the profile DB fetch is still in flight.
  if (!initialized) return <BrandLoadingScreen />

  return (
    <AppErrorBoundary>
    <PlatformRatesSync />
    <OfflineBanner />
    <PwaInstallBanner />
    <CookieConsent />
    <Suspense fallback={<BrandLoadingScreen />}>
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/instrutores" element={<StudentAware><InstructorsPage /></StudentAware>} />
        <Route path="/instrutores/:slug" element={<StudentAware><InstructorProfilePage /></StudentAware>} />
        <Route path="/instrutores-de-direcao/:uf/:cidade" element={<StudentAware><CityInstructorsPage /></StudentAware>} />
        <Route path="/como-funciona" element={<HowItWorksPage />} />
        <Route path="/seja-instrutor" element={<BeInstructorPage />} />
        <Route path="/ajuda" element={<HelpPage />} />
        <Route path="/politica-cancelamento" element={<CancellationPolicyPage />} />
        <Route path="/seguranca" element={<SecurityPage />} />
        <Route path="/termos" element={<TermsPage />} />
        <Route path="/privacidade" element={<PrivacyPage />} />
        <Route path="/presentes" element={<GiftCardPage />} />
        <Route path="/pagamento/:chargeId" element={<PixPaymentPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/certificado" element={<CertificateVerifyPage />} />
        <Route path="/certificado/:numero" element={<CertificateVerifyPage />} />
      </Route>

      {/* Auth routes (no layout) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/cadastro" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/google-calendar/callback" element={<GoogleCalendarCallbackPage />} />

      {/* Student routes */}
      <Route
        element={
          <ProtectedRoute>
            <RoleRoute role="student">
              <StudentLayout />
            </RoleRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/aluno/dashboard" element={<StudentDashboard />} />
        <Route path="/aluno/instrutores" element={<InstructorsPage embedded />} />
        <Route path="/aluno/instrutores/:slug" element={<InstructorProfilePage />} />
        <Route path="/aluno/agendar/:instructorId" element={<BookingFlow />} />
        <Route path="/aluno/aulas/:bookingId" element={<StudentBookingDetail />} />
        <Route path="/aluno/perfil" element={<StudentProfile />} />
        <Route path="/aluno/avaliacoes" element={<StudentReviews />} />
        <Route path="/aluno/mensagens" element={<MessagesPage />} />
        <Route path="/aluno/pagamentos" element={<StudentPayments />} />
        <Route path="/aluno/notificacoes" element={<StudentNotificationSettings />} />
        <Route path="/aluno/notificacoes/inbox" element={<NotificationsPage />} />
        <Route path="/aluno/premium" element={<StudentPremium />} />
        <Route path="/aluno/simulado" element={<StudentQuiz />} />
        <Route path="/aluno/conquistas" element={<Navigate to="/aluno/progresso" replace />} />
        <Route path="/aluno/pacotes" element={<StudentPackages />} />
        <Route path="/aluno/carteira" element={<StudentWallet />} />
        <Route path="/aluno/progresso" element={<StudentProgress />} />
        <Route path="/aluno/indicacoes" element={<StudentReferrals />} />
        <Route path="/aluno/certificados" element={<StudentCertificates />} />
        <Route path="/aluno/historico" element={<StudentHistory />} />
        <Route path="/aluno/suporte"   element={<StudentSupport />} />
      </Route>

      {/* Instructor routes */}
      <Route
        element={
          <ProtectedRoute>
            <RoleRoute role="instructor">
              <InstructorLayout />
            </RoleRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/instrutor/dashboard" element={<InstructorDashboard />} />
        <Route path="/instrutor/agenda" element={<InstructorAgenda />} />
        <Route path="/instrutor/aulas" element={<InstructorLessons />} />
        <Route path="/instrutor/financeiro" element={<PageTabs tabs={[
          { value: 'repasses', label: 'Repasses e aulas', content: <InstructorFinancial /> },
          { value: 'ganhos', label: 'Análise de ganhos', content: <InstructorEarnings /> },
        ]} />} />
        <Route path="/instrutor/perfil" element={<InstructorEditProfile />} />
        <Route path="/instrutor/alunos" element={<InstructorStudents />} />
        <Route path="/instrutor/mensagens" element={<MessagesPage />} />
        <Route path="/instrutor/notificacoes" element={<InstructorNotificationSettings />} />
        <Route path="/instrutor/notificacoes/inbox" element={<NotificationsPage />} />
        {/* Instrutor Pro descontinuado (2026-10-11): links antigos vão ao painel. */}
        <Route path="/instrutor/pro" element={<Navigate to="/instrutor/dashboard" replace />} />
        <Route path="/instrutor/relatorio" element={<InstructorReport />} />
        <Route path="/instrutor/saques" element={<Navigate to="/instrutor/financeiro" replace />} />
        <Route path="/instrutor/ganhos" element={<Navigate to="/instrutor/financeiro?aba=ganhos" replace />} />
        <Route path="/instrutor/certificados" element={<InstructorIssueCertificate />} />
        <Route path="/instrutor/pacotes"  element={<InstructorPackages />} />
        <Route path="/instrutor/veiculos" element={<InstructorVehicles />} />
        <Route path="/instrutor/suporte"  element={<InstructorSupport />} />
        <Route path="/instrutor/galeria"  element={<InstructorGallery />} />
        <Route path="/instrutor/chat-templates" element={<InstructorChatSettings />} />
      </Route>

      {/* Student onboarding (no full layout) */}
      <Route
        path="/aluno/onboarding"
        element={
          <ProtectedRoute>
            <RoleRoute role="student">
              <StudentOnboarding />
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Instructor onboarding (no full layout) */}
      <Route
        path="/instrutor/onboarding"
        element={
          <ProtectedRoute>
            <RoleRoute role="instructor">
              <InstructorOnboarding />
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        element={
          <ProtectedRoute>
            <RoleRoute role="admin">
              <AdminLayout />
            </RoleRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/instrutores" element={<AdminInstructors />} />
        <Route path="/admin/disputas" element={<AdminDisputes />} />
        <Route path="/admin/financeiro" element={<PageTabs tabs={[
          { value: 'visao', label: 'Visão geral', content: <AdminFinancial /> },
          { value: 'repasses', label: 'Repasses aos instrutores', content: <AdminPayouts /> },
        ]} />} />
        <Route path="/admin/cupons" element={<AdminCoupons />} />
        <Route path="/admin/usuarios" element={<AdminUsers />} />
        <Route path="/admin/usuarios/:userId" element={<AdminUserDetail />} />
        <Route path="/admin/configuracoes" element={<AdminPlatformSettings />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/instrutores/aprovacao" element={<AdminInstructorApproval />} />
        <Route path="/admin/exportar" element={<AdminExports />} />
        <Route path="/admin/pagamentos" element={<Navigate to="/admin/financeiro?aba=repasses" replace />} />
        <Route path="/admin/cupons-promo" element={<Navigate to="/admin/cupons" replace />} />
        <Route path="/admin/anuncios" element={<AdminAnnouncements />} />
        <Route path="/admin/auditoria" element={<AdminAuditLog />} />
        <Route path="/admin/suporte"        element={<AdminSupport />} />
        <Route path="/admin/faq"            element={<AdminFaq />} />
        <Route path="/admin/notificacoes"   element={<AdminNotifications />} />
        <Route path="/admin/lgpd"           element={<AdminLgpd />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    </AppErrorBoundary>
  )
}

export default App
